/**
 * artefacts.ts — calendar (.ics) and Apple Wallet pass as EMAIL SETTINGS, not
 * delivery channels (operator ruling, CD prompt 2026-09-01 §1).
 *
 * Both were channel chips in the "Channels · at least one" picker, which made
 * "how does the customer get their calendar entry?" a question you answered by
 * enabling a channel and then separately placing a block. They are one control
 * each now, with three states, on the email channel:
 *
 *     off  ·  attachment  ·  download link
 *
 * WHY ON THE EMAIL CHANNEL and not the per-language design document: the .ics
 * and the pass are the SAME artefact in every language, so a setting on the
 * design would have to be held identical across languages by hand and would
 * drift the first time someone edited only the German one. And not on the flow:
 * a message is attached to a flow, but its artefacts belong to the message.
 *
 * WHY WALLET HAS NO `attachment` (§5): not the plumbing — attaching a signed
 * pass moves a credential off a revocable, access-controlled download path into
 * an email that gets forwarded, archived and searched. That depends on how an
 * event credential is represented (JAI-1283 / JAI-1322). Until it settles the
 * option is ABSENT, not disabled with a tooltip — a gate is absence.
 */

export type ArtefactMode = "off" | "attachment" | "link";
export type ArtefactId = "calendar" | "wallet";

export interface ArtefactSettings {
  calendar: ArtefactMode;
  wallet: ArtefactMode;
}

/** The option set per artefact. Same SHAPE, different options — see the header. */
export const ARTEFACT_MODES: Record<ArtefactId, ArtefactMode[]> = {
  calendar: ["off", "attachment", "link"],
  wallet: ["off", "link"],
};

export const ARTEFACT_MODE_LABEL: Record<ArtefactMode, string> = {
  off: "Off",
  attachment: "Attached",
  link: "Download link",
};

/** One line per state, in the operator's terms — what the CUSTOMER gets. */
/* `Partial` per artefact, not a full Record: wallet has no `attachment` state by
 * ruling (§5 above — a signed pass is a credential, never attached), so a type
 * demanding a hint for it would demand copy for an option that must not exist.
 * The type follows the ruling; a reader keys by a mode the artefact offers
 * (`ARTEFACT_MODES`) and gets a string. */
export const ARTEFACT_MODE_HINT: Record<ArtefactId, Partial<Record<ArtefactMode, string>>> = {
  calendar: {
    off: "No calendar entry. Nothing to add to their calendar from this email.",
    attachment: "A real .ics file on the email — their mail client offers to add it directly.",
    link: "An add-to-calendar link they tap. Point a Button at the calendar token, or drop the Calendar links block for .ics, Google and Apple side by side.",
  },
  wallet: {
    off: "No wallet pass.",
    link: "An Add-to-Apple-Wallet badge over a credential URL — the way a pass reaches a customer today.",
  },
};

/** The catalog link token each artefact's URL resolves through (§4). ONE
 *  vocabulary: a Button target and an SMS token resolve to the identical URL. */
export const ARTEFACT_TOKEN: Record<ArtefactId, string> = {
  calendar: "calendar_ics_url",
  wallet: "wallet_pass_url",
};

/** Whether the artefact's placeholder resolves at all. On `off` and
 *  `attachment` there is NO URL — a Button pointing at it, or a token in an
 *  SMS body, has nothing to resolve to (§4). */
export const artefactUrlResolves = (mode: ArtefactMode): boolean => mode === "link";

/**
 * Read the settings off a message, migrating what the old pseudo-channels meant.
 *
 * Calendar defaults to `attachment` because the .ics attachment ships today and
 * is UNCONDITIONAL — the control exposes what already happens, so nothing
 * already authored changes behaviour the day this lands. Wallet reads its old
 * chip: a message that had the wallet pseudo-channel on was reaching customers
 * by credential URL, which is exactly `link`.
 */
export function artefactsOf(m: { artefacts?: Partial<ArtefactSettings>; channels?: string[] }): ArtefactSettings {
  const stored = m.artefacts || {};
  const legacy = m.channels || [];
  return {
    calendar: stored.calendar ?? "attachment",
    wallet: stored.wallet ?? (legacy.indexOf("wallet") > -1 ? "link" : "off"),
  };
}

/** Modes an artefact may legitimately hold — used to refuse a stored value that
 *  is no longer offered (a `wallet: "attachment"` row written by a future build,
 *  or by hand) rather than rendering an option the ruling removed. */
export const artefactModeAllowed = (id: ArtefactId, mode: ArtefactMode): boolean =>
  ARTEFACT_MODES[id].indexOf(mode) > -1;
